// Aggregate insights — §7 + R-100 (≥5-member threshold, k-anonymity enforced server-side).
// R-102: privacy note required on every insights screen.
// R-101: differencing suppression applied by the backend for group rollups.

import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getOrgInsights,
  getGroupInsights,
  getTeamInsights,
  triggerExport,
  exportDownloadUrl,
  listGroups,
  listTeams,
  type OrgInsights,
  type GroupInsights,
  type TeamInsights,
  type Group,
  type Team,
} from '../lib/api';

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

type Tab = 'org' | 'groups' | 'teams';

function TabBar({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const tabs: { id: Tab; label: string }[] = [
    { id: 'org', label: 'Overview' },
    { id: 'groups', label: 'Groups' },
    { id: 'teams', label: 'Teams' },
  ];
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', marginBottom: 'var(--space-6)' }}>
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            borderRadius: 6,
            border: active === t.id ? '1.5px solid var(--color-accent)' : '1.5px solid var(--color-border)',
            background: active === t.id ? 'var(--color-accent-light)' : 'transparent',
            color: active === t.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
            fontWeight: active === t.id ? 600 : 400,
            fontSize: '0.875rem',
            cursor: 'pointer',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Privacy note (R-102: required on every insights screen)
// ---------------------------------------------------------------------------

function PrivacyNote() {
  return (
    <div
      className="card"
      style={{
        marginBottom: 'var(--space-6)',
        background: 'var(--color-accent-light)',
        border: '1px solid #a7d4bc',
      }}
      role="note"
    >
      <p style={{ fontSize: '0.875rem', color: 'var(--color-accent)' }}>
        <strong>Who can see what:</strong> All metrics here are aggregated —
        no individual data is ever shown. Groups and teams with fewer than 5
        contributing members show "Not enough activity yet" rather than a
        partial count (R-100). Engine Check results are never shown at individual
        granularity and are excluded from org-level exports (R-168). No metric
        identifies whether a specific person opted out (R-167).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function pct(v: number | null): string {
  if (v === null) return '—';
  return `${Math.round(v * 100)}%`;
}

function SuppressedNote() {
  return (
    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
      Not enough activity yet to show this privately (fewer than 5 contributing members).
    </p>
  );
}

function InsightCard({
  label,
  value,
  description,
  status,
}: {
  label: string;
  value: string;
  description: string;
  status?: 'good' | 'warn';
}) {
  return (
    <div className="card">
      <p
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--color-text-muted)',
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: '2rem',
          fontWeight: 700,
          marginTop: 'var(--space-2)',
          lineHeight: 1,
          color:
            status === 'good'
              ? 'var(--color-success)'
              : status === 'warn'
              ? 'var(--color-warning)'
              : 'var(--color-text)',
        }}
      >
        {value}
      </p>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-subtle)', marginTop: 'var(--space-2)' }}>
        {description}
      </p>
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <h2
      style={{
        fontSize: '0.8125rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--color-text-muted)',
        marginBottom: 'var(--space-3)',
        marginTop: 'var(--space-8)',
      }}
    >
      {children}
    </h2>
  );
}

// ---------------------------------------------------------------------------
// Engine distribution bar (R-210: opt-out excluded from both sides)
// ---------------------------------------------------------------------------

function EngineBandBar({
  lowPct,
  justRightPct,
  highPct,
  sharedCount,
}: {
  lowPct: number | null;
  justRightPct: number | null;
  highPct: number | null;
  sharedCount: number | null;
}) {
  if (lowPct === null || justRightPct === null || highPct === null) return null;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          height: 12,
          borderRadius: 6,
          overflow: 'hidden',
          marginBottom: 'var(--space-2)',
          gap: 2,
        }}
      >
        <div
          style={{ flex: lowPct, background: '#3b82f6', minWidth: lowPct > 0 ? 2 : 0 }}
          title={`Running low: ${pct(lowPct)}`}
        />
        <div
          style={{ flex: justRightPct, background: '#22c55e', minWidth: justRightPct > 0 ? 2 : 0 }}
          title={`Running well: ${pct(justRightPct)}`}
        />
        <div
          style={{ flex: highPct, background: '#ef4444', minWidth: highPct > 0 ? 2 : 0 }}
          title={`Running hot: ${pct(highPct)}`}
        />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-4)', fontSize: '0.8125rem', color: 'var(--color-text-subtle)' }}>
        <span>🔵 Low {pct(lowPct)}</span>
        <span>🟢 Well {pct(justRightPct)}</span>
        <span>🔴 Hot {pct(highPct)}</span>
        {sharedCount !== null && (
          <span style={{ marginLeft: 'auto' }}>{sharedCount} shared entries</span>
        )}
      </div>
      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
        Opt-outs are excluded from both sides (R-210) — this distribution cannot move by opting out.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Org overview tab
// ---------------------------------------------------------------------------

function OrgTab({ insights, orgId }: { insights: OrgInsights; orgId: string }) {
  const [exporting, setExporting] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      const { exportId: id } = await triggerExport(orgId);
      setExportId(id);
    } catch {
      setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <SectionHeader>Membership</SectionHeader>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
        <InsightCard
          label="Total members"
          value={insights.totalMembers.toLocaleString()}
          description="Active members across the org"
        />
        <InsightCard
          label="Active last 30 days"
          value={insights.activeMembers30d.toLocaleString()}
          description="Members with at least one engine entry, check-in answer, or kudos"
        />
        <InsightCard
          label="Pending approvals"
          value={insights.pendingApprovals.toLocaleString()}
          description="Join requests waiting for review"
          status={insights.pendingApprovals > 0 ? 'warn' : 'good'}
        />
      </div>

      <SectionHeader>Engagement (last 30 days)</SectionHeader>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
        <InsightCard
          label="Coach messages"
          value={insights.coachMessages30d.toLocaleString()}
          description="Total coach interactions across the org"
        />
        <InsightCard
          label="Kudos given"
          value={insights.kudos30d.toLocaleString()}
          description="Recognitions sent across the org"
        />
      </div>

      {/* Engine distribution (R-211) */}
      <SectionHeader>Engine distribution (last 30 days)</SectionHeader>
      <div className="card">
        {insights.engineDistributionSuppressed ? (
          <SuppressedNote />
        ) : (
          <EngineBandBar
            lowPct={insights.engineLowPct}
            justRightPct={insights.engineJustRightPct}
            highPct={insights.engineHighPct}
            sharedCount={insights.engineSharedCount}
          />
        )}
      </div>

      {/* Group summary */}
      {insights.groupSummary.length > 0 && (
        <>
          <SectionHeader>Groups</SectionHeader>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-surface-secondary)' }}>
                  {['Group', 'Teams', 'Members'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--color-text-muted)',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {insights.groupSummary.map((g, i) => (
                  <tr
                    key={g.id}
                    style={{ borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}
                  >
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontWeight: 500 }}>{g.name}</td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-subtle)' }}>{g.teamCount}</td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-subtle)' }}>{g.memberCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Export (R-103–R-105) */}
      <SectionHeader>Data export</SectionHeader>
      <div className="card">
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-subtle)', marginBottom: 'var(--space-4)' }}>
          Export aggregate insights and the audit log as JSON. The same k-anonymity
          rules apply — no individual data is included (R-104). The download link
          is valid for 24 hours.
        </p>
        {exportId ? (
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-success)', marginBottom: 'var(--space-2)' }}>
              Export ready — link expires in 24 hours.
            </p>
            <a
              href={exportDownloadUrl(orgId, exportId)}
              download
              style={{
                display: 'inline-block',
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-accent)',
                color: '#fff',
                borderRadius: 6,
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              Download export
            </a>
          </div>
        ) : (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-secondary"
          >
            {exporting ? 'Generating…' : 'Export data'}
          </button>
        )}
        {exportError && (
          <p style={{ marginTop: 'var(--space-2)', fontSize: '0.875rem', color: 'var(--color-error, #dc2626)' }}>
            {exportError}
          </p>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Groups tab
// ---------------------------------------------------------------------------

function GroupsTab({ orgId }: { orgId: string }) {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [insights, setInsights] = useState<GroupInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listGroups(orgId).then(setGroups).catch(() => setGroups([]));
  }, [orgId]);

  useEffect(() => {
    if (!selectedGroupId) { setInsights(null); return; }
    setLoading(true);
    setError(null);
    getGroupInsights(selectedGroupId)
      .then((i) => { setInsights(i); setLoading(false); })
      .catch(() => { setError('Failed to load group insights.'); setLoading(false); });
  }, [selectedGroupId]);

  const activeGroups = groups?.filter((g) => !g.archivedAt) ?? [];

  return (
    <>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          style={{
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 6,
            border: '1px solid var(--color-border)',
            fontSize: '0.875rem',
            minWidth: 240,
          }}
        >
          <option value="">Select a group…</option>
          {activeGroups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      </div>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
      {error && <p style={{ color: 'var(--color-error, #dc2626)', fontSize: '0.875rem' }}>{error}</p>}

      {insights && !loading && (
        <>
          <SectionHeader>Group rollup</SectionHeader>
          <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
            {insights.rollupSuppressed ? (
              <>
                <SuppressedNote />
                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
                  The rollup is also suppressed to prevent differencing attacks (R-101) —
                  removing one team's data from the group total would reveal fewer than 5 contributors.
                </p>
              </>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-4)' }}>
                <InsightCard
                  label="Active this week"
                  value={(insights.rollupActiveMembersThisWeek ?? 0).toLocaleString()}
                  description="Unique members with activity in the last 7 days"
                />
                <InsightCard
                  label="Kudos (30d)"
                  value={(insights.rollupKudos30d ?? 0).toLocaleString()}
                  description="Kudos sent within this group"
                />
                <InsightCard
                  label="Check-in answers (30d)"
                  value={(insights.rollupCheckinParticipants30d ?? 0).toLocaleString()}
                  description="Unique members who answered the check-in question"
                />
              </div>
            )}
          </div>

          {!insights.rollupSuppressed && (insights.rollupEngineLowPct !== null) && (
            <>
              <SectionHeader>Engine distribution (group, last 30 days)</SectionHeader>
              <div className="card">
                <EngineBandBar
                  lowPct={insights.rollupEngineLowPct}
                  justRightPct={insights.rollupEngineJustRightPct}
                  highPct={insights.rollupEngineHighPct}
                  sharedCount={null}
                />
              </div>
            </>
          )}

          {/* Adoption curve */}
          {insights.adoptionCurve.length > 0 && (
            <>
              <SectionHeader>12-week adoption curve</SectionHeader>
              <div className="card">
                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
                  Unique active members per ISO week. Weeks with fewer than 5 active members are suppressed (R-100).
                </p>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
                  {insights.adoptionCurve.map((w) => {
                    const maxVal = Math.max(...insights.adoptionCurve.map((x) => x.activeMembers ?? 0), 1);
                    const h = w.activeMembers !== null ? Math.round((w.activeMembers / maxVal) * 72) : 0;
                    return (
                      <div
                        key={w.week}
                        title={`${w.week}: ${w.activeMembers !== null ? w.activeMembers : 'suppressed'} active`}
                        style={{
                          flex: 1,
                          height: w.activeMembers !== null ? `${h}px` : 4,
                          background: w.activeMembers !== null ? 'var(--color-accent)' : 'var(--color-border)',
                          borderRadius: '3px 3px 0 0',
                          minHeight: 4,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Per-team breakdown */}
          <SectionHeader>Teams in this group</SectionHeader>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-surface-secondary)' }}>
                  {['Team', 'Members', 'Active (7d)', 'Kudos (30d)', 'Check-ins (30d)'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--color-text-muted)',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {insights.teams.map((t, i) => (
                  <tr key={t.id} style={{ borderTop: i > 0 ? '1px solid var(--color-border)' : 'none' }}>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontWeight: 500 }}>{t.name}</td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-subtle)' }}>{t.memberCount}</td>
                    {t.suppressed ? (
                      <td colSpan={3} style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                        Not enough activity yet (&lt;5 members)
                      </td>
                    ) : (
                      <>
                        <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-subtle)' }}>
                          {t.activeMembersThisWeek ?? 0}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-subtle)' }}>
                          {t.kudos30d ?? 0}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--color-text-subtle)' }}>
                          {t.checkinParticipants30d ?? 0}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!selectedGroupId && !loading && (
        <div className="empty-state">
          <p className="empty-state__title">Select a group to view its insights</p>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Teams tab
// ---------------------------------------------------------------------------

function PrincipleMixBar({
  mix,
}: {
  mix: { feltSafety: number; connection: number; selfRegulation: number };
}) {
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 'var(--space-2)', gap: 2 }}>
        <div style={{ flex: mix.feltSafety, background: '#8b5cf6' }} title={`Felt safety: ${pct(mix.feltSafety)}`} />
        <div style={{ flex: mix.connection, background: '#06b6d4' }} title={`Connection: ${pct(mix.connection)}`} />
        <div style={{ flex: mix.selfRegulation, background: '#f59e0b' }} title={`Self-regulation: ${pct(mix.selfRegulation)}`} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-4)', fontSize: '0.8125rem', color: 'var(--color-text-subtle)' }}>
        <span>🟣 Felt-safety {pct(mix.feltSafety)}</span>
        <span>🔵 Connection {pct(mix.connection)}</span>
        <span>🟡 Self-regulation {pct(mix.selfRegulation)}</span>
      </div>
    </div>
  );
}

function TeamsTab({ orgId }: { orgId: string }) {
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [insights, setInsights] = useState<TeamInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listTeams(orgId).then(setTeams).catch(() => setTeams([]));
  }, [orgId]);

  useEffect(() => {
    if (!selectedTeamId) { setInsights(null); return; }
    setLoading(true);
    setError(null);
    getTeamInsights(selectedTeamId)
      .then((i) => { setInsights(i); setLoading(false); })
      .catch(() => { setError('Failed to load team insights.'); setLoading(false); });
  }, [selectedTeamId]);

  const activeTeams = teams?.filter((t) => !t.archivedAt) ?? [];

  return (
    <>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <select
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          style={{
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 6,
            border: '1px solid var(--color-border)',
            fontSize: '0.875rem',
            minWidth: 240,
          }}
        >
          <option value="">Select a team…</option>
          {activeTeams.map((t) => (
            <option key={t.id} value={t.id}>{t.name} ({t.groupName})</option>
          ))}
        </select>
      </div>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
      {error && <p style={{ color: 'var(--color-error, #dc2626)', fontSize: '0.875rem' }}>{error}</p>}

      {insights && !loading && (
        <>
          {insights.suppressed ? (
            <div className="card">
              <SuppressedNote />
            </div>
          ) : (
            <>
              <SectionHeader>Activity (last 30 days)</SectionHeader>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-4)' }}>
                <InsightCard
                  label="Members"
                  value={insights.memberCount.toLocaleString()}
                  description="Total team members"
                />
                <InsightCard
                  label="Active this week"
                  value={(insights.activeMembersThisWeek ?? 0).toLocaleString()}
                  description="Members with activity in the last 7 days"
                />
                <InsightCard
                  label="Kudos given"
                  value={(insights.kudosGiven30d ?? 0).toLocaleString()}
                  description="Recognitions sent by team members"
                />
                <InsightCard
                  label="Kudos received"
                  value={(insights.kudosReceived30d ?? 0).toLocaleString()}
                  description="Recognitions received by team members"
                />
                <InsightCard
                  label="Check-in answers"
                  value={(insights.checkinParticipants30d ?? 0).toLocaleString()}
                  description="Unique members who answered the check-in question"
                />
              </div>

              {/* Principle mix from kudos */}
              {insights.principleMix30d && (
                <>
                  <SectionHeader>Principle mix (kudos, last 30 days)</SectionHeader>
                  <div className="card">
                    <PrincipleMixBar mix={insights.principleMix30d} />
                  </div>
                </>
              )}

              {/* Engine distribution (R-210) */}
              <SectionHeader>Engine distribution (last 30 days)</SectionHeader>
              <div className="card">
                {insights.engineSharedCount === null ? (
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                    No shared engine entries in the last 30 days.
                  </p>
                ) : (
                  <EngineBandBar
                    lowPct={insights.engineLowPct}
                    justRightPct={insights.engineJustRightPct}
                    highPct={insights.engineHighPct}
                    sharedCount={insights.engineSharedCount}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      {!selectedTeamId && !loading && (
        <div className="empty-state">
          <p className="empty-state__title">Select a team to view its insights</p>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function InsightsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('org');
  const [orgInsights, setOrgInsights] = useState<OrgInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    getOrgInsights(user.orgId)
      .then((i) => { setOrgInsights(i); setLoading(false); })
      .catch((e: unknown) => {
        const status = (e as { status?: number }).status;
        if (status === 404) {
          setError('Insights require admin or owner role.');
        } else {
          setError('Failed to load insights. Try refreshing.');
        }
        setLoading(false);
      });
  }, [user]);

  if (!user) return null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Insights</h1>
          <p className="page-subtitle">Aggregate adoption data for {user.orgName}.</p>
        </div>
      </div>

      <PrivacyNote />

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {!loading && error && (
        <div
          className="card"
          style={{
            background: 'var(--color-error-light, #fef2f2)',
            border: '1px solid var(--color-error, #ef4444)',
          }}
          role="alert"
        >
          <p style={{ fontSize: '0.875rem', color: 'var(--color-error, #dc2626)' }}>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <TabBar active={tab} onChange={setTab} />

          {tab === 'org' && orgInsights && (
            <OrgTab insights={orgInsights} orgId={user.orgId} />
          )}
          {tab === 'org' && !orgInsights && (
            <div className="empty-state">
              <p className="empty-state__title">No insights available yet</p>
              <p className="empty-state__body">
                Insights appear once teams have been active. Groups and teams need at least
                5 contributing members before aggregate data is shown (R-100).
              </p>
            </div>
          )}
          {tab === 'groups' && <GroupsTab orgId={user.orgId} />}
          {tab === 'teams' && <TeamsTab orgId={user.orgId} />}
        </>
      )}
    </>
  );
}
